import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { NAV, COMPANY } from "@/data/site";
import { EASE } from "@/components/motion/Reveal";
import { getLenis } from "@/lib/lenis";
import { INTRO_DELAY } from "@/lib/intro";
import { cn } from "@/lib/utils";

const MobileMenu = ({ onClose }) => (
  <motion.div
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.7, ease: EASE }}
    className="fixed inset-0 z-[60] flex flex-col bg-ink-950 px-6 pb-10 pt-6 lg:hidden"
    data-testid="mobile-menu"
  >
    <div className="flex items-center justify-between">
      <Logo />
      <button onClick={onClose} className="grid h-11 w-11 place-items-center rounded-full border border-white/15" data-testid="mobile-menu-close" aria-label="Close menu">
        <X className="h-5 w-5" />
      </button>
    </div>
    <nav className="mt-14 flex flex-col">
      {NAV.map((n, i) => (
        <motion.div key={n.to} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.06, duration: 0.6, ease: EASE }}>
          <NavLink to={n.to} end={n.to === "/"} onClick={onClose} data-testid={`mobile-nav-${n.id}`} className={({ isActive }) => cn("flex items-baseline gap-4 border-b border-white/5 py-4 font-display text-4xl font-bold", isActive ? "text-white" : "text-slate-500")}>
            <span className="font-mono text-xs text-volt">0{i + 1}</span>
            {n.label}
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <a href={COMPANY.tel} className="btn-primary mt-auto h-14 text-base" data-testid="mobile-menu-call">
      <Phone className="h-4 w-4" /> Call {COMPANY.phone}
    </a>
  </motion.div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const l = getLenis();
    if (l) open ? l.stop() : l.start();
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.2 + INTRO_DELAY }}
        className={cn("fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,padding] duration-500", scrolled ? "border-white/5 bg-ink-950/80 py-3 backdrop-blur-xl" : "border-transparent bg-ink-950/30 py-5 backdrop-blur-md")}
        data-testid="site-header"
      >
        <div className={cn("hidden overflow-hidden border-b border-white/5 transition-[max-height,opacity,margin] duration-500 lg:block", scrolled ? "max-h-0 border-transparent opacity-0" : "-mt-5 mb-4 max-h-10 opacity-100")} data-testid="header-top-bar">
          <div className="container-x flex h-9 items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-volt" /> Over 65 years in business · Revenue share · Equipment sales · Service</span>
            <span className="flex items-center gap-6">
              <Link to="/service" className="transition-colors hover:text-volt" data-testid="topbar-service-link">Request service</Link>
              <span>{COMPANY.street}, McMinnville TN</span>
            </span>
          </div>
        </div>
        <div className="container-x flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link" aria-label="Brewer Amusement home">
            <Logo />
          </Link>
          <nav className="hidden items-center lg:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-link-${n.id}`} className={({ isActive }) => cn("relative px-3.5 py-2 text-sm transition-colors duration-300", isActive ? "text-white" : "text-slate-400 hover:text-white")}>
                {({ isActive }) => (
                  <>
                    {n.label}
                    {isActive && <motion.span layoutId="nav-active" className="absolute inset-x-3.5 -bottom-0.5 h-px bg-volt" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-6 lg:flex">
            <a href={COMPANY.tel} data-testid="header-phone-link" className="flex items-center gap-2 font-mono text-xs tracking-wider text-slate-300 transition-colors hover:text-volt">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-signal" /> {COMPANY.phone}
            </a>
            <Link to="/partner" data-testid="header-cta-button" className="btn-primary h-10 px-5 text-sm">
              Become a Partner <ArrowRight className="btn-arrow h-4 w-4" />
            </Link>
          </div>
          <button onClick={() => setOpen(true)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 lg:hidden" data-testid="mobile-menu-toggle" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </motion.header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
